import produce from 'immer';

import { createKeyNotInTransform } from 'Lib/persist/transforms';
import type { Action } from 'Lib/reduxType';

import { getCommonResetAction, LOGOUT } from 'Store/commonActions';
import {
  CLEAR_ELIGIBILITY,
  CLEAR_GIFT_CARD,
  GET_GIFT_CARD_ELIGIBILITY,
  GET_INCENTIVE_ELIGIBILITY,
  GIFT_CARD_REDEEM_SUCCESS,
  INCENTIVE_REDEEM_ERROR,
  INCENTIVE_REDEEM_SUCCESS,
} from 'Store/incentives/constants';
import { GUEST_LOGIN_SUCCESS, LOGIN_SUCCESS } from 'Store/mfa/constants';
import { PURCHASE_SUCCESSFUL_POST } from 'Store/purchase/constants';
import {
  CLEAR_DEEPLINK_NAVIGATION_INFO,
  CLEAR_DEEPLINK_OTP,
  CLEAR_DEEPLINK_PROMO,
  DEEPLINK_TYPE_INCENTIVE,
  GOLD_DEEPLINK_ACTIVE,
  GOLD_DEEPLINK_SUCCESS,
  REQUEST_AGE_VERIFICATION_URL_ERROR,
  REQUEST_AGE_VERIFICATION_URL_SUCCESS,
  SET_APPEALABLE_BAN_CHALLENGE_TOKEN,
  SET_ARKOSE_DATA_BLOB,
  SET_DEEPLINK_INCENTIVES_INFO,
  SET_DEEPLINK_INTRO_PRICE_INFO,
  SET_DEEPLINK_NAVIGATION_INFO,
  SET_DEEPLINK_PROMO,
  SET_DEEPLINK_VERIFICATION_INFO,
  SET_IS_UNDERAGE_ONBOARDING,
  SET_SELFIE_VERIFICATION_ATTEMPTED,
  SET_UNDERAGE_TOKEN,
  STORE_POST_LOGIN_REDIRECT_PATH,
} from 'Store/user/constants';

import initialState from './initialState';
import type { UserState } from './types';

export const reducer = produce<UserState, [Action]>((draft, action) => {
  switch (action.type) {
    // Login related
    case LOGIN_SUCCESS:
      draft.loggedIn = true;
      break;
    case GUEST_LOGIN_SUCCESS:
      draft.loggedInGuest = true;
      break;
    case LOGOUT:
      draft.loggingOut = true;
      break;
    case CLEAR_DEEPLINK_OTP:
      draft.deeplinkOtp = null;
      draft.deeplinkType = null;
      break;

    case SET_APPEALABLE_BAN_CHALLENGE_TOKEN:
      draft.appealChallengeToken = action.payload;
      break;
    case SET_ARKOSE_DATA_BLOB:
      draft.arkoseDataBlob = action.payload;
      break;

    // Age verifications
    case REQUEST_AGE_VERIFICATION_URL_SUCCESS:
      draft.ageVerificationUrl = action.payload.url;
      draft.ageVerificationUrlTs = action.payload.expiration_ts;
      draft.ageVerificationUrlError = null;
      break;
    case REQUEST_AGE_VERIFICATION_URL_ERROR:
      draft.ageVerificationUrlError = action.payload.errorCode;
      break;

    // Underage onboarding
    case SET_UNDERAGE_TOKEN:
      draft.underageToken = action.payload;
      break;
    case SET_IS_UNDERAGE_ONBOARDING:
      draft.isUnderageOnboarding = action.payload;
      break;

    case PURCHASE_SUCCESSFUL_POST:
      draft.hasPlus = true;
      break;

    // Request Verification
    case SET_SELFIE_VERIFICATION_ATTEMPTED:
      draft.selfieVerificationAttempted = action?.payload ?? false;
      break;

    // Generic deeplink
    case SET_DEEPLINK_VERIFICATION_INFO:
      draft.deeplinkOtp = action.payload.otp;
      draft.deeplinkType = action.payload.type;
      break;

    // Navigation deeplink
    case SET_DEEPLINK_NAVIGATION_INFO:
      draft.navigationRoute = action.payload.route;
      break;

    case STORE_POST_LOGIN_REDIRECT_PATH:
      draft.postLoginRedirectPath = action.payload.path;
      draft.postLoginRedirectPushState = action.payload.pushState;
      break;

    case CLEAR_DEEPLINK_NAVIGATION_INFO:
      draft.navigationRoute = null;
      break;

    // Revenue starts here
    // Gold deeplink
    case GOLD_DEEPLINK_ACTIVE:
      draft.isGoldDeeplinkActive = true;
      break;
    case GOLD_DEEPLINK_SUCCESS:
      draft.isGoldDeeplinkActive = false;
      break;

    // Intro price
    case SET_DEEPLINK_INTRO_PRICE_INFO:
      draft.isGoldDeeplinkActive = true;
      draft.introPriceDiscount = action.payload.discount ?? 0;
      draft.introPriceTerm = action.payload.term ?? 0;
      break;

    // Incentive
    case SET_DEEPLINK_INCENTIVES_INFO:
      draft.promoCode = action.payload.promoCode;
      draft.deeplinkType = DEEPLINK_TYPE_INCENTIVE;
      break;
    case GET_INCENTIVE_ELIGIBILITY:
      draft.promoCode = action.payload.promoCode;
      break;
    case INCENTIVE_REDEEM_SUCCESS:
    case INCENTIVE_REDEEM_ERROR:
    case CLEAR_ELIGIBILITY:
      draft.promoCode = null;
      break;
    case CLEAR_GIFT_CARD:
      draft.giftCardNumber = null;
      draft.giftCardPin = null;
      break;
    case SET_DEEPLINK_PROMO:
      draft.showPromo = true;
      break;
    case CLEAR_DEEPLINK_PROMO:
      draft.showPromo = false;
      break;

    case GET_GIFT_CARD_ELIGIBILITY:
      draft.giftCardNumber = action.payload.cardNumber;
      draft.giftCardPin = action.payload.cardPin;
      break;
    case GIFT_CARD_REDEEM_SUCCESS:
      break;

    case getCommonResetAction(action.type):
      return initialState;
    default:
      return draft;
  }
}, initialState);

export const persistConfig = {
  version: 2,
  transforms: [
    // @TODO: This should probably be an allowlist instead
    createKeyNotInTransform([
      'appealChallengeToken',
      'loggingOut',
      'introPriceDiscount',
      'introPriceTerm',
      'isExistingUserFromOnboarding',
      'isGoldDeeplinkActive',
      'isUnderageOnboarding',
      'deeplinkOtp',
      'deeplinkType',
      'postLoginRedirectPath',
      'promoCode',
      'showPromo',
      'underageToken',
    ]),
  ],
  wait: 100,
};
